import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchMessageThreads,
  fetchThreadMessages,
  sendMessage,
  markMessagesAsRead,
  fetchUnreadCount,
  MessageThread,
  MessagesResponse,
} from '../thunks/messagesThunks';

interface ThreadMessage {
  id: string;
  content: string;
  senderId: string;
  timestamp: string;
  read?: boolean;
  isSpotlighted?: boolean;
}

interface MessagesState {
  threads: MessageThread[];
  activeThread: {
    id: string | null;
    messages: ThreadMessage[];
  };
  unreadTotal: number;
  loading: {
    threads: boolean;
    messages: boolean;
    sending: boolean;
  };
  error: string | null;
}

const initialState: MessagesState = {
  threads: [],
  activeThread: {
    id: null,
    messages: [],
  },
  unreadTotal: 0,
  loading: {
    threads: false,
    messages: false,
    sending: false,
  },
  error: null,
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    clearActiveThread: (state) => {
      state.activeThread = {
        id: null,
        messages: [],
      };
    },
    removeThread: (state, action: PayloadAction<string>) => {
      const threadId = action.payload;
      state.threads = state.threads.filter(t => t.id !== threadId);
      if (state.activeThread.id === threadId) {
        state.activeThread = { id: null, messages: [] };
      }
    },
    addOptimisticMessage: (state, action: PayloadAction<ThreadMessage & { threadId?: string }>) => {
      const message = action.payload;

      // Add to active thread if it matches
      if (state.activeThread.id && (message.threadId === state.activeThread.id || !message.threadId)) {
        state.activeThread.messages.push(message);
      }

      // Update thread in threads list
      if (message.threadId) {
        const thread = state.threads.find(t => t.id === message.threadId);
        if (thread) {
          // Update last message
          thread.lastMessage = {
            id: message.id,
            content: message.content,
            senderId: message.senderId,
            timestamp: message.timestamp,
          };
          // Increment unread count if message is from someone else
          // (We'll determine this based on whether it's in active thread)
          if (state.activeThread.id !== message.threadId) {
            thread.unreadCount += 1;
            state.unreadTotal += 1;
          }
        }
      }
    },
    updateMessageReadStatus: (state, action: PayloadAction<{ messageId: string; read: boolean }>) => {
      const message = state.activeThread.messages.find(m => m.id === action.payload.messageId);
      if (message) {
        message.read = action.payload.read;
      }
    },
    // Inbound message pushed by the server via socket. Updates the thread's
    // lastMessage, bumps unread counters when the user isn't currently
    // viewing that thread, and appends to activeThread.messages when they
    // are so the new message shows up live.
    receiveSocketMessage: (state, action: PayloadAction<ThreadMessage & { threadId: string }>) => {
      const m = action.payload;
      // Skip if we already have this id anywhere (server can echo to thread room
      // and user room for the same recipient).
      const already = state.activeThread.messages.some((x) => x.id === m.id);
      const inActiveThread = state.activeThread.id === m.threadId;

      if (inActiveThread && !already) {
        state.activeThread.messages.push({
          id: m.id,
          content: m.content,
          senderId: m.senderId,
          timestamp: m.timestamp,
          read: m.read ?? false,
          // Defensive: accept either casing from the socket payload
          isSpotlighted: Boolean((m as any).is_spotlighted ?? m.isSpotlighted),
        });
      }

      const thread = state.threads.find((t) => t.id === m.threadId);
      if (thread) {
        thread.lastMessage = {
          id: m.id,
          content: m.content,
          senderId: m.senderId,
          timestamp: m.timestamp,
        };
        if (!inActiveThread) {
          thread.unreadCount += 1;
          state.unreadTotal += 1;
        }
      } else {
        // Thread not in cache — bump global unread so the badge still updates;
        // the thread list will hydrate on next fetch.
        state.unreadTotal += 1;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch message threads
    builder
      .addCase(fetchMessageThreads.pending, (state) => {
        state.loading.threads = true;
        state.error = null;
      })
      .addCase(fetchMessageThreads.fulfilled, (state, action: PayloadAction<MessagesResponse>) => {
        state.loading.threads = false;
        state.threads = action.payload.threads;
        state.unreadTotal = action.payload.unreadTotal;
      })
      .addCase(fetchMessageThreads.rejected, (state, action) => {
        state.loading.threads = false;
        state.error = action.payload as string;
      });

    // Fetch thread messages
    builder
      .addCase(fetchThreadMessages.pending, (state) => {
        state.loading.messages = true;
        state.error = null;
      })
      .addCase(fetchThreadMessages.fulfilled, (state, action) => {
        state.loading.messages = false;
        // Transform backend response to match frontend types.
        // Server may return either snake (is_spotlighted) or camel
        // (isSpotlighted) depending on the path, so accept either.
        const messages = action.payload.messages.map((msg: any) => ({
          id: msg.id,
          content: msg.content,
          senderId: msg.sender_id,
          timestamp: msg.created_at || msg.timestamp,
          read: msg.read || false,
          isSpotlighted: Boolean(msg.is_spotlighted ?? msg.isSpotlighted),
        }));
        state.activeThread = {
          id: action.payload.threadId,
          messages,
        };
      })
      .addCase(fetchThreadMessages.rejected, (state, action) => {
        state.loading.messages = false;
        state.error = action.payload as string;
      });

    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading.sending = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading.sending = false;
        // Message should already be in the list from optimistic update
        // Update with real ID if needed
        if (action.payload.data) {
          const d = action.payload.data;
          const messageData = {
            id: d.id,
            content: d.content,
            senderId: d.sender_id,
            timestamp: d.created_at,
            // Server returns both snake and camel; either is authoritative.
            isSpotlighted: Boolean(d.is_spotlighted ?? d.isSpotlighted),
          };

          // Replace temp message in active thread
          const index = state.activeThread.messages.findIndex(
            (msg) => msg.id.startsWith('temp-')
          );
          if (index !== -1) {
            state.activeThread.messages[index] = messageData;
          }

          // Also update thread in threads list (replace temp message ID with real one)
          if (action.payload.data.thread_id || state.activeThread.id) {
            const threadId = action.payload.data.thread_id || state.activeThread.id;
            const thread = state.threads.find(t => t.id === threadId);
            if (thread) {
              thread.lastMessage = {
                id: messageData.id,
                content: messageData.content,
                senderId: messageData.senderId,
                timestamp: messageData.timestamp,
              };
            }
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading.sending = false;
        state.error = action.payload as string;
        // Remove optimistic message on failure from both active thread and threads list
        state.activeThread.messages = state.activeThread.messages.filter(
          (msg) => !msg.id.startsWith('temp-')
        );

        // Remove temp message from threads list
        state.threads.forEach(thread => {
          if (thread.lastMessage?.id?.startsWith('temp-')) {
            thread.lastMessage = { id: '', content: '', senderId: '', timestamp: '' };
          }
        });
      });

    // Mark messages as read
    builder
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        // Update thread unread count
        const thread = state.threads.find((t) => t.id === action.payload.threadId);
        if (thread) {
          state.unreadTotal -= thread.unreadCount;
          thread.unreadCount = 0;
        }
      });

    // Fetch unread count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action: PayloadAction<number>) => {
      state.unreadTotal = action.payload;
    });
  },
});

export const { clearActiveThread, addOptimisticMessage, updateMessageReadStatus, removeThread, receiveSocketMessage } = messagesSlice.actions;
export default messagesSlice.reducer;
